import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";

const ResetPassword = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [linkInvalid, setLinkInvalid] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let resolved = false;
    const markReady = () => {
      if (resolved) return;
      resolved = true;
      setIsReady(true);
    };

    const cleanUrl = () => {
      try {
        window.history.replaceState(null, "", window.location.pathname);
      } catch {
        // ignore
      }
    };

    // Set up listener FIRST so we don't miss PASSWORD_RECOVERY
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "PASSWORD_RECOVERY") {
        markReady();
        cleanUrl();
        return;
      }
      if (event === "SIGNED_IN" && session) {
        markReady();
      }
    });

    const init = async () => {
      const search = new URLSearchParams(window.location.search);
      const hash = window.location.hash || "";

      // PKCE / OTP-style recovery link: ?token_hash=...&type=recovery
      const tokenHash = search.get("token_hash");
      const queryType = search.get("type");
      if (tokenHash && queryType === "recovery") {
        const { error } = await supabase.auth.verifyOtp({
          type: "recovery",
          token_hash: tokenHash,
        });
        if (error) {
          setLinkInvalid(true);
          return;
        }
        markReady();
        cleanUrl();
        return;
      }

      // Legacy hash-style recovery link: #access_token=...&type=recovery
      if (hash.includes("type=recovery")) {
        markReady();
        // Defer cleanup so Supabase can finish processing the hash
        setTimeout(cleanUrl, 250);
        return;
      }

      // Already-authenticated recovery session (e.g. needs_password_change)
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        markReady();
        return;
      }
    };
    init();

    // Fallback: if nothing resolves within 10s, treat link as invalid/expired
    const timeout = setTimeout(() => {
      if (!resolved) {
        setLinkInvalid(true);
      }
    }, 10000);

    return () => {
      subscription.unsubscribe();
      clearTimeout(timeout);
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setLoading(true);

    try {
      const { error: passwordError } = await supabase.auth.updateUser({
        password,
        data: { needs_password_change: false },
      });

      if (passwordError) throw passwordError;

      // Sign out the recovery session so the user logs in fresh with their new password
      await supabase.auth.signOut();

      toast.success("Password updated! Please sign in with your new password.");
      navigate("/auth");
    } catch (error: any) {
      const msg = error?.message || "An error occurred";
      if (msg.toLowerCase().includes("session") || msg.toLowerCase().includes("expired") || msg.toLowerCase().includes("token")) {
        toast.error("Your reset link has expired. Please request a new one.");
      } else {
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  if (linkInvalid) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <main className="container mx-auto px-4 py-20">
          <div className="max-w-md mx-auto">
            <Card>
              <CardHeader>
                <CardTitle>Invalid or Expired Link</CardTitle>
                <CardDescription>
                  Your password reset link is invalid or has expired. Please request a new one.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Link to="/auth">
                  <Button className="w-full">Back to Login</Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!isReady) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <main className="container mx-auto px-4 py-20">
          <div className="max-w-md mx-auto text-center">
            <p className="text-muted-foreground">Verifying your session...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <main className="container mx-auto px-4 py-20">
        <div className="max-w-md mx-auto">
          <Card>
            <CardHeader>
              <CardTitle>Set New Password</CardTitle>
              <CardDescription>
                Please choose a new password for your account.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="password">New Password</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm New Password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={6}
                  />
                </div>
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? "Updating..." : "Set Password"}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ResetPassword;
